import { useEffect, useState, useRef, useCallback } from "react";
import api, { formatApiError, API } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { corporateInput, EmptyState } from "@/components/Shared";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import useSmartPoll from "@/lib/useSmartPoll";
import {
  ChatCircle, Plus, PaperPlaneRight, CheckCircle, ArrowCounterClockwise,
  Wrench, MagnifyingGlass, Paperclip, ImageSquare, X, FileText,
  DownloadSimple, ArrowsClockwise, Circle, FilePdf, Lock, PushPin, Hash,
  Users, Megaphone, Briefcase, CaretDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  useTypingIndicator, useCommsPolling, useMentionableUsers,
  PriorityChip, ThreadMetaPanel, TypingDots, TemplatesMenu,
  MentionAutocomplete, ReadReceipt, MessageBody,
} from "./comms/CommsPro";

// ---------- Helpers ----------
function timeAgo(iso, lang = "es") {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  const units = lang === "en"
    ? { s: "s ago", m: "m ago", h: "h ago", d: "d ago" }
    : { s: "s", m: "m", h: "h", d: "d" };
  const prefix = lang === "en" ? "" : "hace ";
  if (s < 60) return `${prefix}${s}${units.s}`;
  if (s < 3600) return `${prefix}${Math.floor(s / 60)}${units.m}`;
  if (s < 86400) return `${prefix}${Math.floor(s / 3600)}${units.h}`;
  return `${prefix}${Math.floor(s / 86400)}${units.d}`;
}

function initials(name) {
  if (!name) return "··";
  const parts = name.trim().split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 0) return "··";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function bytesFmt(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

// ---------- Avatar ----------
function Avatar({ name, side, size = 32 }) {
  // 'service' (Financial-side label for support) or 'support' (Support's own label) use same color.
  const isSupportSide = side === "support" || side === "service";
  const fg = isSupportSide ? "text-amber-600 dark:text-amber-400" : "text-indigo-600 dark:text-indigo-400";
  const bg = isSupportSide ? "bg-amber-50 dark:bg-amber-950/30 ring-amber-200 dark:ring-amber-800" : "bg-indigo-50 dark:bg-indigo-950/30 ring-indigo-200 dark:ring-indigo-800";
  return (
    <div
      className={`inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold text-[11px] ${bg} ${fg}`}
      style={{ width: size, height: size }}
      aria-label={name}
    >
      {initials(name)}
    </div>
  );
}

// ---------- Attachment chip ----------
function AttachmentChip({ att }) {
  const isImage = att.content_type?.startsWith("image/");
  const isPdf = att.content_type === "application/pdf" || att.filename?.toLowerCase().endsWith(".pdf");
  const url = `${API}/threads/files/${att.file_id}`;

  if (isImage) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block max-w-[240px] rounded border border-slate-200 dark:border-slate-700 overflow-hidden hover:opacity-90 transition-opacity" data-testid={`att-${att.file_id}`}>
        <img src={url} alt={att.filename} className="w-full h-auto block" loading="lazy" />
        <div className="px-2 py-1 text-[10px] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 truncate flex items-center justify-between gap-2">
          <span className="truncate">{att.filename}</span>
          <DownloadSimple size={10} />
        </div>
      </a>
    );
  }

  const Icon = isPdf ? FilePdf : FileText;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-2 px-2.5 py-2 border border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500 rounded text-xs transition-colors max-w-[280px]"
      data-testid={`att-${att.file_id}`}
    >
      <Icon size={18} className={isPdf ? "text-red-500" : "text-slate-500"} weight="regular" />
      <div className="min-w-0">
        <div className="font-medium text-slate-900 dark:text-slate-100 truncate">{att.filename}</div>
        <div className="text-[10px] text-slate-500 dark:text-slate-400">{bytesFmt(att.size)}</div>
      </div>
      <DownloadSimple size={12} className="text-slate-400 shrink-0" />
    </a>
  );
}

// ---------- Search overlay ----------
function SearchOverlay({ open, onClose, onPickThread }) {
  const { lang } = useI18n();
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ threads: [], messages: [] });
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(""); setResults({ threads: [], messages: [] }); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setResults({ threads: [], messages: [] }); return; }
    setBusy(true);
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get("/threads/search", { params: { q: q.trim() } });
        setResults(data);
      } catch (err) { console.warn("[threads search] failed:", err?.message); }
      finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(id);
  }, [q, open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && open) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-start pt-[10vh] px-4" onClick={onClose} data-testid="search-overlay">
      <div
        className="w-full max-w-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl rounded-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center gap-3">
          <MagnifyingGlass size={18} className="text-slate-400" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={lang === "en" ? "Search threads and messages…" : "Buscar hilos y mensajes…"}
            className="flex-1 bg-transparent outline-none text-sm text-slate-900 dark:text-white placeholder:text-slate-400"
            data-testid="search-input"
          />
          <kbd className="text-[10px] font-mono text-slate-400 border border-slate-200 dark:border-slate-700 px-1.5 py-0.5 rounded">ESC</kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-2">
          {q.trim().length < 2 && (
            <div className="py-12 text-center text-xs text-slate-400">
              {lang === "en" ? "Type at least 2 characters" : "Escribe al menos 2 caracteres"}
            </div>
          )}
          {q.trim().length >= 2 && !busy && results.threads.length === 0 && results.messages.length === 0 && (
            <div className="py-12 text-center text-xs text-slate-400">
              {lang === "en" ? "No results" : "Sin resultados"}
            </div>
          )}

          {results.threads.length > 0 && (
            <div className="mb-3">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{lang === "en" ? "Threads" : "Hilos"}</div>
              {results.threads.map((t) => (
                <button
                  key={t.id}
                  onClick={() => { onPickThread(t.id); onClose(); }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded text-sm flex items-center gap-2"
                  data-testid={`search-result-thread-${t.id}`}
                >
                  <ChatCircle size={14} className="text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 dark:text-slate-100 truncate">{t.subject}</div>
                    <div className="text-[11px] text-slate-500 truncate">{t.last_message_preview}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {results.messages.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{lang === "en" ? "Messages" : "Mensajes"}</div>
              {results.messages.map((m) => (
                <button
                  key={m.id}
                  onClick={() => { onPickThread(m.thread_id); onClose(); }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded text-sm"
                  data-testid={`search-result-msg-${m.id}`}
                >
                  <div className="text-[11px] text-slate-500 mb-0.5">{m.thread_subject} · {m.sender_name || m.sender_email}</div>
                  <div className="text-sm text-slate-900 dark:text-slate-100 line-clamp-2">{m.match_preview}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Main page ----------
const FILTERS_KEY = [
  { v: "open", es: "Abiertos", en: "Open" },
  { v: "resolved", es: "Resueltos", en: "Resolved" },
  { v: "all", es: "Todos", en: "All" },
];

export default function CommunicationsPage() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [filter, setFilter] = useState("open");
  const [source, setSource] = useState("all"); // all | human | system
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]); // File[]
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);
  const [dmOpen, setDmOpen] = useState(false);
  const [dmForm, setDmForm] = useState({ recipient_email: "", initial_message: "" });
  const [searchOpen, setSearchOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", type: "general", client_id: "", initial_message: "" });
  const [clients, setClients] = useState([]);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const imgInputRef = useRef(null);
  const textareaRef = useRef(null);

  // PRO features
  const mentionableUsers = useMentionableUsers();
  const { typers, notifyTyping } = useTypingIndicator(activeId, user?.email);
  const commsTotals = useCommsPolling(useCallback(() => { loadThreadsRef.current?.(); }, []));
  const loadThreadsRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      if (source !== "all") params.source = source;
      const { data } = await api.get("/threads", { params });
      setThreads(data);
    } catch (err) { console.warn("[loadThreads] failed:", err?.message); }
  }, [filter, source]);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { loadThreadsRef.current = loadThreads; }, [loadThreads]);
  useSmartPoll(loadThreads, 30000);

  useEffect(() => {
    // try to load clients (admin) or support clients (agents)
    api.get("/clients").then(r => setClients(r.data)).catch(() => {
      api.get("/support/clients").then(r => setClients(r.data)).catch(() => setClients([]));
    });
  }, []);

  // Keyboard shortcut ⌘+K or Ctrl+K to open search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const openThread = async (id) => {
    setActiveId(id);
    try {
      const { data } = await api.get(`/threads/${id}/messages`);
      setActiveThread(data.thread);
      setMessages(data.messages);
      loadThreads();
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 80);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const refreshActive = async () => {
    if (!activeId) return;
    const { data } = await api.get(`/threads/${activeId}/messages`);
    setActiveThread(data.thread);
    setMessages(data.messages);
  };

  const addFiles = (files) => {
    const arr = Array.from(files || []);
    const MAX = 10 * 1024 * 1024;
    const ok = arr.filter((f) => {
      if (f.size > MAX) {
        toast.error(`${f.name} supera 10 MB`);
        return false;
      }
      return true;
    });
    setPendingFiles((curr) => [...curr, ...ok]);
  };

  const send = async () => {
    if ((!draft.trim() && pendingFiles.length === 0) || !activeId) return;
    setSending(true);
    try {
      if (pendingFiles.length > 0) {
        const fd = new FormData();
        fd.append("body", draft);
        for (const f of pendingFiles) fd.append("files", f);
        await api.post(`/threads/${activeId}/messages/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post(`/threads/${activeId}/messages`, { body: draft });
      }
      setDraft("");
      setPendingFiles([]);
      await refreshActive();
      loadThreads();
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 80);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const createThread = async () => {
    try {
      const { data } = await api.post("/threads", { ...form, client_id: form.client_id || null });
      toast.success(lang === "en" ? "Thread created" : "Hilo creado");
      setOpen(false); setForm({ subject: "", type: "general", client_id: "", initial_message: "" });
      loadThreads(); openThread(data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleResolved = async () => {
    if (!activeThread) return;
    const next = activeThread.status === "open" ? "resolved" : "open";
    try {
      await api.patch(`/threads/${activeId}/status`, { status: next });
      setActiveThread({ ...activeThread, status: next });
      loadThreads();
      toast.success(next === "resolved" ? (lang === "en" ? "Case resolved" : "Caso resuelto") : (lang === "en" ? "Case reopened" : "Caso reabierto"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const mySide = user?.role === "admin" || user?.role === "super_admin" ? "financial" : "support";

  // Drag and drop
  const [dragOver, setDragOver] = useState(false);
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (!activeId) return;
    addFiles(e.dataTransfer.files);
  };

  return (
    <PageShell
      section={t("nav.group.communication")}
      page={t("nav.communications")}
      context={`${threads.length} ${lang === "en" ? "threads" : "hilos"}`}
      testid="comms-toolbar"
      toolbarExtra={
        <button
          onClick={() => setSearchOpen(true)}
          className="inline-flex items-center gap-2 px-2.5 py-1.5 text-xs text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500 transition-colors rounded"
          data-testid="open-search"
        >
          <MagnifyingGlass size={13} />
          <span>{lang === "en" ? "Search" : "Buscar"}</span>
          <kbd className="text-[10px] font-mono text-slate-400 ml-2">⌘K</kbd>
        </button>
      }
      actions={
        <div className="flex items-center gap-2">
          <Button onClick={() => setDmOpen(true)} variant="outline" className="h-9 text-xs" data-testid="new-dm">
            <Lock size={13} className="mr-1.5" /> {lang === "en" ? "New DM" : "Mensaje privado"}
          </Button>
          <Button onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white h-9" data-testid="new-thread">
            <Plus size={15} className="mr-1.5" /> {lang === "en" ? "New thread" : "Nuevo hilo"}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4 h-[calc(100vh-180px)] min-h-[520px]">
        {/* ─────────── SIDEBAR: Channel · People · Cases ─────────── */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex flex-col overflow-hidden">
          {/* ── SECTION 1: General channel (pinned) ── */}
          {(() => {
            const generalThread = threads.find(t => t.is_broadcast && (t.channel_key === "general" || !t.channel_key));
            if (!generalThread) return null;
            const active = activeId === generalThread.id;
            const unread = generalThread.unread_for_me > 0;
            return (
              <button
                onClick={() => openThread(generalThread.id)}
                data-testid={`thread-item-${generalThread.id}`}
                className={`w-full text-left px-3.5 py-3 border-b border-slate-200 dark:border-slate-800 transition-colors ${
                  active
                    ? "bg-amber-50/70 dark:bg-amber-950/20 border-l-2 border-l-amber-500"
                    : "bg-slate-50/40 dark:bg-slate-900/60 hover:bg-slate-50 dark:hover:bg-slate-800/40 border-l-2 border-l-transparent"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                    active ? "bg-amber-500 text-white" : "bg-slate-900 dark:bg-slate-700 text-white"
                  }`}>
                    <Megaphone size={16} weight="fill" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-amber-700 dark:text-amber-400 font-semibold">Canal general</div>
                      {unread && <Circle size={7} weight="fill" className="text-amber-500 shrink-0" data-testid={`unread-${generalThread.id}`} />}
                    </div>
                    <div className={`text-[13px] mt-0.5 ${unread ? "font-semibold text-slate-900 dark:text-white" : "font-medium text-slate-800 dark:text-slate-100"}`}>
                      # General
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-0.5">{generalThread.last_message_preview || "Sin mensajes aún"}</div>
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono shrink-0 mt-1">{timeAgo(generalThread.last_message_at, lang)}</div>
                </div>
              </button>
            );
          })()}

          {/* ── SECTION 2: People (team quick DM) ── */}
          {mentionableUsers.length > 0 && (
            <div className="border-b border-slate-200 dark:border-slate-800">
              <div className="px-3.5 pt-3 pb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users size={13} className="text-slate-400" />
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">{lang === "en" ? "People" : "Personas"}</div>
                  <span className="text-[10px] font-mono text-slate-400">{mentionableUsers.filter(u => u.email !== user?.email).length}</span>
                </div>
                <button
                  onClick={() => setDmOpen(true)}
                  className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-900 dark:hover:text-white font-semibold inline-flex items-center gap-1"
                  data-testid="new-dm-inline"
                  title={lang === "en" ? "New private message" : "Nuevo mensaje privado"}
                >
                  <Plus size={11} weight="bold" /> {lang === "en" ? "DM" : "DM"}
                </button>
              </div>
              {/* Avatar strip — horizontally scrollable */}
              <div className="px-3.5 pb-3 flex items-center gap-2 overflow-x-auto scrollbar-thin">
                {mentionableUsers
                  .filter(u => u.email !== user?.email)
                  .slice(0, 12)
                  .map(u => {
                    const dm = threads.find(t => t.is_direct && (t.participants || []).includes(u.email));
                    const unread = dm?.unread_for_me > 0;
                    const active = dm && activeId === dm.id;
                    return (
                      <button
                        key={u.email}
                        onClick={() => {
                          if (dm) openThread(dm.id);
                          else { setDmForm({ recipient_email: u.email, initial_message: "" }); setDmOpen(true); }
                        }}
                        data-testid={`people-${u.email}`}
                        className={`relative flex flex-col items-center gap-1 shrink-0 group focus:outline-none`}
                        title={`${u.name || u.email} · ${u.role}`}
                      >
                        <div className={`relative rounded-full ring-2 transition-all ${active ? "ring-amber-500" : "ring-transparent group-hover:ring-slate-300 dark:group-hover:ring-slate-600"}`}>
                          <Avatar name={u.name || u.email} side={["admin","super_admin","limited_admin"].includes(u.role) ? "financial" : "support"} size={34} />
                          {unread && (
                            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-amber-500 ring-2 ring-white dark:ring-slate-900" />
                          )}
                        </div>
                        <div className="text-[9.5px] text-slate-600 dark:text-slate-400 max-w-[54px] truncate font-medium">
                          {(u.name || u.email).split(" ")[0].split("@")[0]}
                        </div>
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

          {/* ── SECTION 3: DMs list (existing conversations) ── */}
          {threads.some(t => t.is_direct) && (
            <div className="border-b border-slate-200 dark:border-slate-800">
              <div className="px-3.5 pt-3 pb-1.5 flex items-center gap-2">
                <Lock size={12} className="text-slate-400" />
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">{lang === "en" ? "Direct messages" : "Mensajes directos"}</div>
                <span className="text-[10px] font-mono text-slate-400 ml-auto">{threads.filter(t => t.is_direct).length}</span>
              </div>
              <div className="max-h-[32vh] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800/60">
                {threads.filter(t => t.is_direct).map(thr => {
                  const active = activeId === thr.id;
                  const otherEmail = (thr.participants || []).find(p => p !== user?.email);
                  const otherUser = mentionableUsers.find(u => u.email === otherEmail);
                  const displayName = otherUser?.name || otherEmail || thr.subject.replace(/^DM · /, "");
                  return (
                    <button
                      key={thr.id}
                      onClick={() => openThread(thr.id)}
                      data-testid={`thread-item-${thr.id}`}
                      className={`w-full text-left px-3.5 py-2.5 transition-colors ${
                        active ? "bg-slate-50 dark:bg-slate-800/60 border-l-2 border-l-amber-400" : "hover:bg-slate-50 dark:hover:bg-slate-800/30 border-l-2 border-l-transparent"
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <Avatar name={displayName} side={otherUser && ["admin","super_admin","limited_admin"].includes(otherUser.role) ? "financial" : "support"} size={30} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            {thr.unread_for_me > 0 && <Circle size={7} weight="fill" className="text-amber-500 shrink-0" />}
                            <div className={`text-[13px] truncate ${thr.unread_for_me > 0 ? "font-semibold text-slate-900 dark:text-white" : "font-medium text-slate-700 dark:text-slate-200"}`}>
                              {displayName}
                            </div>
                          </div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{thr.last_message_preview}</div>
                        </div>
                        <div className="text-[10px] text-slate-400 font-mono shrink-0">{timeAgo(thr.last_message_at, lang)}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── SECTION 4: Cases & general threads ── */}
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            <div className="px-3.5 pt-3 pb-2 flex items-center gap-2">
              <Briefcase size={12} className="text-slate-400" />
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">{lang === "en" ? "Threads & cases" : "Hilos y casos"}</div>
              <span className="text-[10px] font-mono text-slate-400 ml-auto">{threads.filter(t => !t.is_broadcast && !t.is_direct).length}</span>
              <button
                onClick={loadThreads}
                className="text-slate-400 hover:text-slate-700 dark:hover:text-white"
                title={lang === "en" ? "Refresh" : "Refrescar"}
                data-testid="refresh-threads"
              >
                <ArrowsClockwise size={12} />
              </button>
            </div>
            {/* Filter chips */}
            <div className="px-3 pb-2 flex items-center gap-1 flex-wrap">
              {FILTERS_KEY.map(f => (
                <button
                  key={f.v}
                  onClick={() => setFilter(f.v)}
                  data-testid={`comm-filter-${f.v}`}
                  className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider rounded transition-colors ${
                    filter === f.v
                      ? "bg-slate-900 dark:bg-slate-700 text-white"
                      : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
                  }`}
                >
                  {f[lang] || f.es}
                </button>
              ))}
              <span className="mx-1 h-3 w-px bg-slate-200 dark:bg-slate-700" />
              {[
                { v: "all",    es: "Todo",     en: "All" },
                { v: "human",  es: "Personas", en: "People" },
                { v: "system", es: "Sistema",  en: "System" },
              ].map(s => (
                <button
                  key={s.v}
                  onClick={() => setSource(s.v)}
                  data-testid={`comm-source-${s.v}`}
                  className={`px-2 py-1 text-[10px] uppercase tracking-wider transition-colors rounded ${
                    source === s.v
                      ? "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 font-semibold"
                      : "text-slate-500 hover:text-slate-900 dark:hover:text-white"
                  }`}
                >
                  {s.v === "system" && <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 mr-1 align-middle" />}
                  {s[lang] || s.es}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
              {threads.filter(t => !t.is_broadcast && !t.is_direct).length === 0 && (
                <div className="p-6">
                  <EmptyState icon={ChatCircle} title={lang === "en" ? "No threads" : "Sin hilos"} message={lang === "en" ? "Start a new conversation." : "Crea un nuevo hilo."} />
                </div>
              )}
              {threads.filter(t => !t.is_broadcast && !t.is_direct).map(thr => {
                const active = activeId === thr.id;
                return (
                  <button
                    key={thr.id}
                    onClick={() => openThread(thr.id)}
                    data-testid={`thread-item-${thr.id}`}
                    className={`w-full text-left px-3.5 py-3 transition-colors ${
                      active ? "bg-slate-50 dark:bg-slate-800/60 border-l-2 border-l-amber-400" : "hover:bg-slate-50 dark:hover:bg-slate-800/30 border-l-2 border-l-transparent"
                    }`}
                  >
                    <div className="flex items-start gap-2.5">
                      <Avatar name={thr.created_by} side={thr.created_by_side} size={28} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          {thr.unread_for_me > 0 && (
                            <Circle size={7} weight="fill" className="text-amber-500 shrink-0" data-testid={`unread-${thr.id}`} />
                          )}
                          {thr.pinned && <PushPin size={11} weight="fill" className="text-amber-500 shrink-0" title="Fijado" />}
                          {thr.type === "case" && <Wrench size={11} weight="regular" className="text-slate-400 shrink-0" />}
                          {thr.auto_generated && (
                            <span className="shrink-0 px-1 py-px text-[8px] font-bold uppercase tracking-wider bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 rounded" title="Auto-generated by system">
                              SYS
                            </span>
                          )}
                          <div className={`text-[13px] truncate ${thr.unread_for_me > 0 ? "font-semibold text-slate-900 dark:text-white" : "font-medium text-slate-700 dark:text-slate-200"}`}>
                            {thr.subject}
                          </div>
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-1">{thr.last_message_preview}</div>
                        <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 flex items-center gap-1.5 flex-wrap">
                          <span>{timeAgo(thr.last_message_at, lang)}</span>
                          {thr.priority && thr.priority !== "medium" && <PriorityChip priority={thr.priority} />}
                          {thr.assignee_email && (
                            <span className="text-[10px] text-slate-500 dark:text-slate-400" title={`Asignado a ${thr.assignee_email}`}>
                              → {thr.assignee_email.split("@")[0]}
                            </span>
                          )}
                          {thr.tags && thr.tags.length > 0 && (
                            <span className="text-[10px] text-slate-500 dark:text-slate-400">
                              {thr.tags.slice(0, 2).map(t => `#${t}`).join(" ")}
                            </span>
                          )}
                          {thr.status === "resolved" && <span className="text-emerald-600 dark:text-emerald-400 font-semibold uppercase tracking-wider">· {lang === "en" ? "Resolved" : "Resuelto"}</span>}
                          {thr.client_name && <span>· {thr.client_name}</span>}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ─────────── MESSAGE PANEL ─────────── */}
        <div
          className={`bg-white dark:bg-slate-900 border ${dragOver ? "border-amber-400 border-dashed" : "border-slate-200 dark:border-slate-800"} flex flex-col overflow-hidden transition-colors`}
          onDragOver={(e) => { e.preventDefault(); if (activeId) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          {!activeThread ? (
            <div className="flex-1 grid place-items-center text-slate-400 text-sm p-8">
              <div className="max-w-sm text-center">
                <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800 grid place-items-center mb-4">
                  <ChatCircle size={26} weight="light" className="text-slate-400" />
                </div>
                <div className="text-slate-700 dark:text-slate-200 font-semibold text-base">
                  {lang === "en" ? "Communications Center" : "Centro de comunicaciones"}
                </div>
                <div className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                  {lang === "en"
                    ? "Select the general channel, a person, or a thread on the left to start conversing."
                    : "Selecciona el canal general, una persona o un hilo a la izquierda para empezar."}
                </div>
                <div className="mt-5 flex items-center justify-center gap-2 text-[11px] text-slate-400">
                  <kbd className="font-mono border border-slate-200 dark:border-slate-700 px-1.5 py-0.5 rounded">⌘K</kbd>
                  <span>{lang === "en" ? "to search" : "para buscar"}</span>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3 bg-white dark:bg-slate-900">
                <div className="min-w-0 flex-1 flex items-center gap-3">
                  {/* Contextual icon */}
                  {activeThread.is_broadcast ? (
                    <div className="w-9 h-9 rounded-lg bg-amber-500 text-white grid place-items-center shrink-0">
                      <Megaphone size={16} weight="fill" />
                    </div>
                  ) : activeThread.is_direct ? (
                    <div className="w-9 h-9 rounded-lg bg-slate-900 dark:bg-slate-700 text-white grid place-items-center shrink-0">
                      <Lock size={15} weight="fill" />
                    </div>
                  ) : activeThread.type === "case" ? (
                    <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 grid place-items-center shrink-0">
                      <Wrench size={15} weight="regular" />
                    </div>
                  ) : (
                    <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 grid place-items-center shrink-0">
                      <ChatCircle size={15} weight="regular" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 truncate">
                      {activeThread.is_broadcast && <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-amber-700 dark:text-amber-400">Canal</span>}
                      {activeThread.is_direct && <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">DM privado</span>}
                      <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                        {activeThread.is_direct
                          ? (() => {
                              const other = (activeThread.participants || []).find(p => p !== user?.email);
                              const otherUser = mentionableUsers.find(u => u.email === other);
                              return otherUser?.name || other || activeThread.subject.replace(/^DM · /, "");
                            })()
                          : activeThread.subject}
                      </div>
                      {!activeThread.is_broadcast && !activeThread.is_direct && <PriorityChip priority={activeThread.priority || "medium"} />}
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                      {activeThread.is_broadcast
                        ? (lang === "en" ? "Team-wide channel · all admins and agents" : "Canal del equipo · todos los admins y agentes")
                        : `${activeThread.created_by} · ${timeAgo(activeThread.created_at, lang)} · ${messages.length} ${lang === "en" ? "messages" : "mensajes"}`}
                      {activeThread.client_name && <> · <span className="font-medium">{activeThread.client_name}</span></>}
                    </div>
                  </div>
                </div>
                {!activeThread.is_broadcast && !activeThread.is_direct && (
                  <button
                    onClick={toggleResolved}
                    data-testid="toggle-resolved"
                    className={`text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 border transition-colors shrink-0 rounded ${
                      activeThread.status === "resolved"
                        ? "border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                        : "border-emerald-400 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                    }`}
                  >
                    {activeThread.status === "resolved"
                      ? <><ArrowCounterClockwise size={11} className="inline mr-1" />{lang === "en" ? "Reopen" : "Reabrir"}</>
                      : <><CheckCircle size={11} className="inline mr-1" />{lang === "en" ? "Resolve" : "Resolver"}</>}
                  </button>
                )}
              </div>

              {/* PRO: Priority + tags + assignee panel — only for cases/threads (not for broadcast/DM) */}
              {!activeThread.is_broadcast && !activeThread.is_direct && (
                <ThreadMetaPanel
                  thread={activeThread}
                  users={mentionableUsers}
                  onPatched={(t) => { setActiveThread(t); loadThreads(); }}
                />
              )}

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-slate-50/60 dark:bg-slate-950/40">
                {messages.map(m => {
                  const isMine = m.sender_email === user?.email;
                  const isFinancial = ["admin", "super_admin"].includes(m.sender_role);
                  const side = isFinancial ? "financial" : "support";
                  return (
                    <div key={m.id} className={`flex items-end gap-2 ${isMine ? "flex-row-reverse" : ""}`} data-testid={`msg-${m.id}`}>
                      {!isMine && <Avatar name={m.sender_name || m.sender_email} side={side} size={28} />}
                      <div className={`max-w-[78%] ${isMine ? "items-end" : "items-start"} flex flex-col gap-1.5`}>
                        {!isMine && (
                          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 px-1">
                            {m.sender_name || m.sender_email} <span className="text-slate-300 dark:text-slate-600">·</span> {isFinancial ? "Financial" : (mySide === "financial" ? "Service" : "Support")}
                          </div>
                        )}
                        {m.body && (
                          <div
                            className={`px-3.5 py-2.5 text-sm break-words ${
                              isMine
                                ? "bg-slate-900 dark:bg-slate-700 text-white"
                                : "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100"
                            }`}
                          >
                            <MessageBody body={m.body} myEmail={user?.email} />
                          </div>
                        )}
                        {m.attachments && m.attachments.length > 0 && (
                          <div className="flex flex-wrap gap-2 max-w-full">
                            {m.attachments.map((att) => <AttachmentChip key={att.file_id} att={att} />)}
                          </div>
                        )}
                        <div className={`text-[10px] text-slate-400 dark:text-slate-500 px-1 flex items-center gap-1.5 ${isMine ? "text-right justify-end" : ""}`}>
                          {timeAgo(m.created_at, lang)}
                          {isMine && <ReadReceipt message={m} myEmail={user?.email} />}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && (
                  <div className="text-center text-slate-400 py-12 text-sm">{lang === "en" ? "No messages yet" : "Sin mensajes aún"}</div>
                )}
              </div>

              {/* Pending attachments preview */}
              {pendingFiles.length > 0 && (
                <div className="px-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex flex-wrap gap-2">
                  {pendingFiles.map((f, i) => (
                    <div key={i} className="inline-flex items-center gap-2 px-2 py-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[11px] text-slate-700 dark:text-slate-200 rounded">
                      {f.type.startsWith("image/") ? <ImageSquare size={12} /> : <FileText size={12} />}
                      <span className="truncate max-w-[180px]">{f.name}</span>
                      <span className="text-slate-400">{bytesFmt(f.size)}</span>
                      <button onClick={() => setPendingFiles((c) => c.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500" data-testid={`remove-file-${i}`}>
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Typing indicator */}
              <TypingDots typers={typers} />

              {/* Composer */}
              <div className="border-t border-slate-200 dark:border-slate-800 p-3 flex items-end gap-2 relative">
                <input ref={fileInputRef} type="file" multiple hidden onChange={(e) => addFiles(e.target.files)} data-testid="file-input"
                       accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/*" />
                <input ref={imgInputRef} type="file" multiple hidden onChange={(e) => addFiles(e.target.files)} accept="image/*" data-testid="img-input" />

                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={activeThread.status === "resolved" || sending}
                  className="p-2 text-slate-500 hover:text-slate-900 dark:hover:text-white disabled:opacity-40 transition-colors"
                  title={lang === "en" ? "Attach file" : "Adjuntar archivo"}
                  data-testid="attach-file"
                >
                  <Paperclip size={16} />
                </button>
                <button
                  onClick={() => imgInputRef.current?.click()}
                  disabled={activeThread.status === "resolved" || sending}
                  className="p-2 text-slate-500 hover:text-slate-900 dark:hover:text-white disabled:opacity-40 transition-colors"
                  title={lang === "en" ? "Attach photo" : "Adjuntar foto"}
                  data-testid="attach-img"
                >
                  <ImageSquare size={16} />
                </button>
                <TemplatesMenu onPick={(body) => setDraft((cur) => (cur ? cur + "\n\n" : "") + body)} />

                <div className="flex-1 relative">
                  <Textarea
                    ref={textareaRef}
                    value={draft}
                    onChange={(e) => { setDraft(e.target.value); notifyTyping(); }}
                    onKeyDown={(e) => {
                      const handled = MentionAutocomplete.handleKeyStatic?.(e);
                      if (handled) return;
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
                    }}
                    placeholder={activeThread.status === "resolved"
                      ? (lang === "en" ? "Thread resolved. Reopen to reply." : "Hilo resuelto. Reábrelo para responder.")
                      : (lang === "en" ? "Write a message… (@ to mention, ⌘+Enter to send)" : "Escribe un mensaje… (@ para mencionar, ⌘+Enter enviar)")}
                    disabled={activeThread.status === "resolved" || sending}
                    rows={1}
                    className="w-full min-h-[40px] max-h-[140px] border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 resize-y text-sm"
                    data-testid="msg-input"
                  />
                  <MentionAutocomplete
                    textareaRef={textareaRef}
                    value={draft}
                    onChange={setDraft}
                    users={mentionableUsers}
                  />
                </div>
                <Button
                  onClick={send}
                  disabled={(!draft.trim() && pendingFiles.length === 0) || activeThread.status === "resolved" || sending}
                  className="bg-slate-900 hover:bg-slate-800 text-white h-10 shrink-0"
                  data-testid="send-msg"
                >
                  <PaperPlaneRight size={15} weight="fill" />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* New thread dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{lang === "en" ? "New thread" : "Nuevo hilo"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{lang === "en" ? "Subject *" : "Asunto *"}</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={corporateInput} data-testid="thread-subject" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{lang === "en" ? "Type" : "Tipo"}</Label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={corporateInput} data-testid="thread-type">
                  <option value="general">{lang === "en" ? "General" : "General"}</option>
                  <option value="case">{lang === "en" ? "Technical case" : "Caso técnico"}</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{lang === "en" ? "Client (optional)" : "Cliente (opcional)"}</Label>
                <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className={corporateInput}>
                  <option value="">—</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{lang === "en" ? "Initial message *" : "Mensaje inicial *"}</Label>
              <Textarea rows={4} value={form.initial_message} onChange={(e) => setForm({ ...form, initial_message: e.target.value })} className="border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" data-testid="thread-initial" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{lang === "en" ? "Cancel" : "Cancelar"}</Button>
            <Button onClick={createThread} disabled={!form.subject || !form.initial_message} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-thread">
              {lang === "en" ? "Create" : "Crear hilo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Direct Message dialog */}
      <Dialog open={dmOpen} onOpenChange={setDmOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock size={16} weight="fill" className="text-slate-500" />
              {lang === "en" ? "New private message" : "Nuevo mensaje privado"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {lang === "en"
                ? "Only you and the recipient can see this conversation."
                : "Solo tú y el destinatario podrán ver esta conversación."}
            </p>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {lang === "en" ? "Recipient *" : "Destinatario *"}
              </Label>
              <select
                value={dmForm.recipient_email}
                onChange={(e) => setDmForm({ ...dmForm, recipient_email: e.target.value })}
                className={corporateInput}
                data-testid="dm-recipient"
              >
                <option value="">— {lang === "en" ? "select a person" : "elige una persona"} —</option>
                {mentionableUsers
                  .filter(u => u.email !== user?.email)
                  .map(u => (
                    <option key={u.email} value={u.email}>
                      {u.name || u.email} · {u.role} · {u.email}
                    </option>
                  ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {lang === "en" ? "Message *" : "Mensaje *"}
              </Label>
              <Textarea
                rows={4}
                value={dmForm.initial_message}
                onChange={(e) => setDmForm({ ...dmForm, initial_message: e.target.value })}
                className="border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                data-testid="dm-message"
                placeholder={lang === "en" ? "Say hi…" : "Escribe algo…"}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDmOpen(false)}>{lang === "en" ? "Cancel" : "Cancelar"}</Button>
            <Button
              onClick={async () => {
                if (!dmForm.recipient_email || !dmForm.initial_message.trim()) {
                  toast.error(lang === "en" ? "Recipient and message required" : "Destinatario y mensaje requeridos");
                  return;
                }
                try {
                  const { data } = await api.post("/threads/direct", dmForm);
                  toast.success(lang === "en" ? "Private message sent" : "Mensaje privado enviado");
                  setDmOpen(false);
                  setDmForm({ recipient_email: "", initial_message: "" });
                  await loadThreads();
                  openThread(data.id);
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail) || "Error");
                }
              }}
              disabled={!dmForm.recipient_email || !dmForm.initial_message.trim()}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="dm-send"
            >
              <Lock size={13} className="mr-1.5" /> {lang === "en" ? "Send private" : "Enviar privado"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} onPickThread={openThread} />
    </PageShell>
  );
}
